import {
  resolveCanonicalTreePostDeletionHistoricalAuthorityConsolidation,
  type CanonicalTreeHistoricalAuthorityConsolidationStatus,
} from './CanonicalTreePostDeletionHistoricalAuthorityConsolidation';

import {
  resolveCanonicalTreePostMigrationHistoricalSourcePhysicalDeletionAuthority,
} from './CanonicalTreePostMigrationHistoricalSourcePhysicalDeletionAuthority';

export interface CanonicalTreeHistoricalAuthorityMilestoneReadModel {
  readonly milestone:
    | 'CC-TREE-ARCH-054'
    | 'CC-TREE-ARCH-055'
    | 'CC-TREE-ARCH-056'
    | 'CC-TREE-ARCH-057'
    | 'CC-TREE-ARCH-058'
    | 'CC-TREE-ARCH-059'
    | 'CC-TREE-ARCH-060'
    | 'CC-TREE-ARCH-061'
    | 'CC-TREE-ARCH-062'
    | 'CC-TREE-ARCH-063'
    | 'CC-TREE-ARCH-064'
    | 'CC-TREE-ARCH-065'
    | 'CC-TREE-ARCH-066';

  readonly status:
    CanonicalTreeHistoricalAuthorityConsolidationStatus;

  readonly interpretation:
    | 'HISTORICAL_MILESTONE_ONLY'
    | 'CURRENT_REPOSITORY_AUTHORITY';

  readonly representsCurrentRepositoryState:
    boolean;

  readonly supersededByMilestone:
    'CC-TREE-ARCH-066' | null;

  readonly originalDecisionPreserved:
    true;
}

export interface CanonicalTreeHistoricalSourceDispositionReadModel {
  readonly candidate:
    | 'BROWSER_SOURCE'
    | 'REACT_STORE'
    | 'ARCH_035_RUNTIME'
    | 'ARCH_042_PARITY';

  readonly sourcePath:
    string;

  readonly physicalRepositoryState:
    'ABSENT';

  readonly activeProductionReferenceState:
    'NONE';

  readonly activeBarrelExportState:
    'ABSENT';

  readonly historicalTruthState:
    'IMMUTABLE_FIXTURE_BACKED';

  readonly deletionAuthorityMilestone:
    'CC-TREE-ARCH-066';
}

export interface CanonicalTreePostDeletionHistoricalAuthorityReadModel {
  readonly milestone:
    'CC-TREE-ARCH-068';

  readonly readModel:
    'POST_DELETION_HISTORICAL_AUTHORITY_READ_MODEL';

  readonly currentRepositoryAuthorityMilestone:
    'CC-TREE-ARCH-066';

  readonly consolidationAuthorityMilestone:
    'CC-TREE-ARCH-067';

  readonly currentPhysicalSourceState:
    'FOUR_RETIRED_SOURCES_ABSENT';

  readonly physicalRepositorySourceCount:
    0;

  readonly deletedHistoricalSourceCount:
    4;

  readonly activeProductionReferenceCount:
    0;

  readonly registryOnlyProductionAuthority:
    true;

  readonly immutableHistoricalTruthPreserved:
    true;

  readonly arch056ArchiveChanged:
    false;

  readonly arch062ArchiveChanged:
    false;

  readonly historicalAuthorityCount:
    12;

  readonly currentAuthorityCount:
    1;

  readonly milestoneCount:
    13;

  readonly historicalStatementsRepresentCurrentState:
    false;

  readonly historicalAuthoritySourcesRewritten:
    false;

  readonly historicalDocumentationRewritten:
    false;

  readonly historicalVerifiersDeleted:
    false;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly certifiesDeploymentReadiness:
    false;

  readonly milestones:
    readonly CanonicalTreeHistoricalAuthorityMilestoneReadModel[];

  readonly sources:
    readonly CanonicalTreeHistoricalSourceDispositionReadModel[];

  readonly nextGovernedDependency:
    'POST_DELETION_HISTORICAL_AUTHORITY_READ_MODEL_CONSUMER_AUDIT';
}

export const readCanonicalTreePostDeletionHistoricalAuthority =
  (): CanonicalTreePostDeletionHistoricalAuthorityReadModel => {
    const deletionAuthority =
      resolveCanonicalTreePostMigrationHistoricalSourcePhysicalDeletionAuthority();

    const consolidationAuthority =
      resolveCanonicalTreePostDeletionHistoricalAuthorityConsolidation();

    const milestones =
      consolidationAuthority.decisions.map(
        decision =>
          Object.freeze({
            milestone:
              decision.milestone,

            status:
              decision.consolidationStatus,

            interpretation:
              decision.representsCurrentPhysicalSourceState
                ? 'CURRENT_REPOSITORY_AUTHORITY' as const
                : 'HISTORICAL_MILESTONE_ONLY' as const,

            representsCurrentRepositoryState:
              decision.representsCurrentPhysicalSourceState,

            supersededByMilestone:
              decision.supersededByMilestone,

            originalDecisionPreserved:
              true as const,
          }),
      );

    const sources =
      deletionAuthority.decisions.map(
        decision =>
          Object.freeze({
            candidate:
              decision.candidate,

            sourcePath:
              decision.sourcePath,

            physicalRepositoryState:
              'ABSENT' as const,

            activeProductionReferenceState:
              'NONE' as const,

            activeBarrelExportState:
              'ABSENT' as const,

            historicalTruthState:
              'IMMUTABLE_FIXTURE_BACKED' as const,

            deletionAuthorityMilestone:
              'CC-TREE-ARCH-066' as const,
          }),
      );

    return Object.freeze({
      milestone:
        'CC-TREE-ARCH-068',

      readModel:
        'POST_DELETION_HISTORICAL_AUTHORITY_READ_MODEL',

      currentRepositoryAuthorityMilestone:
        deletionAuthority.milestone,

      consolidationAuthorityMilestone:
        consolidationAuthority.milestone,

      currentPhysicalSourceState:
        'FOUR_RETIRED_SOURCES_ABSENT',

      physicalRepositorySourceCount:
        consolidationAuthority.physicalRepositorySourceCount,

      deletedHistoricalSourceCount:
        deletionAuthority.deletedCandidateCount,

      activeProductionReferenceCount:
        deletionAuthority.activeProductionReferenceCount,

      registryOnlyProductionAuthority:
        deletionAuthority.registryOnlyProductionAuthorityPreserved,

      immutableHistoricalTruthPreserved:
        consolidationAuthority.immutableHistoricalTruthPreserved,

      arch056ArchiveChanged:
        consolidationAuthority.arch056ArchiveChanged,

      arch062ArchiveChanged:
        consolidationAuthority.arch062ArchiveChanged,

      historicalAuthorityCount:
        consolidationAuthority.historicalMilestoneCount,

      currentAuthorityCount:
        consolidationAuthority.currentPhysicalDeletionAuthorityCount,

      milestoneCount:
        consolidationAuthority.consolidatedAuthorityCount,

      historicalStatementsRepresentCurrentState:
        false,

      historicalAuthoritySourcesRewritten:
        consolidationAuthority.historicalAuthoritiesRewritten,

      historicalDocumentationRewritten:
        consolidationAuthority.historicalDocumentsRewritten,

      historicalVerifiersDeleted:
        consolidationAuthority.historicalVerifiersDeleted,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      certifiesDeploymentReadiness:
        false,

      milestones:
        Object.freeze(milestones),

      sources:
        Object.freeze(sources),

      nextGovernedDependency:
        'POST_DELETION_HISTORICAL_AUTHORITY_READ_MODEL_CONSUMER_AUDIT',
    });
  };
